// assets/hardware-preview.js
// Visual-only hardware preview (spec 2026-09-24-remove-hardware-variants).
// Delegated on document because the variant morph replaces the gallery (media-gallery.js
// #handleVariantUpdate -> replaceWith) and does not re-run connectedCallback. Loaded from
// layout/theme.liquid so it also works in quick-add dialogs, whose injected scripts never run.
// Everything is scoped to the nearest <dialog> (quick-add) or the page, so a quick-add on a
// product page never drives the page's own gallery.

/** @type {Map<Document | Element, string>} */
const selectedByScope = new Map();
/** Set when the shopper picked an image in the zoom dialog: the gallery then follows it with a
 *  programmatic select, which is a real choice even though it is not userInitiated. */
let zoomSelectionPending = false;

/** @param {Element | null | undefined} el */
function scopeOf(el) {
  return el?.closest('dialog') ?? document;
}

/** @param {Document | Element} scope @param {string} name */
function syncSwatches(scope, name) {
  for (const b of scope.querySelectorAll('[data-hardware-preview-swatch]')) {
    b.setAttribute('aria-pressed', String(b.getAttribute('data-hardware-preview-swatch') === name));
  }
  for (const el of scope.querySelectorAll('[data-hardware-preview-value]')) el.textContent = name;
}

/** @param {Document | Element} scope @param {string} name */
function showInGallery(scope, name) {
  const g = scope.querySelector('media-gallery');
  if (!g) return;
  const slides = [...g.querySelectorAll('slideshow-slide[data-hardware]')];
  const index = slides.findIndex((s) => s.getAttribute('data-hardware') === name);
  // Smooth scrolling is broken page-wide on this theme; select instantly.
  if (index >= 0) g.slideshow?.select(index, undefined, { animate: false });
}

/**
 * The thumbnails show the current finish, but the variant morph does not re-render this block.
 * Copy the swatches from the section HTML the variant picker already fetched.
 * @param {Document | Element} scope
 * @param {Document | undefined} html
 */
function refreshThumbnails(scope, html) {
  const fresh = html?.querySelector?.('[data-hardware-preview] .hardware-preview__swatches');
  const current = scope.querySelector('[data-hardware-preview] .hardware-preview__swatches');
  if (!fresh || !current) return;
  current.replaceChildren(...[...fresh.childNodes].map((node) => document.importNode(node, true)));
  const name = selectedByScope.get(scope);
  if (name) syncSwatches(scope, name);
}

/** @param {Document | Element} scope */
function apply(scope) {
  const name = selectedByScope.get(scope);
  if (!name) return;
  syncSwatches(scope, name);
  showInGallery(scope, name);
}

if (!window.__hardwarePreviewInit) {
  window.__hardwarePreviewInit = true;

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-hardware-preview-swatch]');
    if (!button) return;
    const scope = scopeOf(button);
    selectedByScope.set(scope, button.getAttribute('data-hardware-preview-swatch'));
    apply(scope);
  });

  // After a finish/size change media-gallery.js swaps in the new gallery from its listener on
  // the section, which runs before this document-level one, so the new slideshow is already
  // connected here. Apply synchronously: requestAnimationFrame never fires in hidden tabs.
  document.addEventListener('variant:update', (event) => {
    const scope = scopeOf(event.target instanceof Element ? event.target : null);
    refreshThumbnails(scope, event.detail?.data?.html);
    apply(scope);
  });

  document.addEventListener('zoom-media:selected', () => {
    zoomSelectionPending = true;
  }, true);

  // Shopper swiped/scrolled the gallery (userInitiated) or picked in the zoom dialog: that is a
  // choice, so the swatch follows. Any other select is the theme repositioning the slideshow
  // (a new gallery settling on slide 0, possibly late: its rAF is deferred while the tab is
  // hidden), so put the shopper's hardware back, whenever it arrives.
  document.addEventListener('slideshow:select', (event) => {
    const slide = event.detail?.slide;
    if (!(slide instanceof Element) || !slide.closest('media-gallery')) return;
    const name = slide.getAttribute('data-hardware');
    const scope = scopeOf(slide);
    const current = selectedByScope.get(scope);
    if (!name) return;
    const zoomChoice = zoomSelectionPending;
    zoomSelectionPending = false;
    if (name === current) return;
    const isChoice = event.detail.userInitiated || zoomChoice;
    if (!isChoice && current) {
      showInGallery(scope, current);
      return;
    }
    selectedByScope.set(scope, name);
    syncSwatches(scope, name);
  });
}
